/**
 * `AIConversationPresentationModel.ts` — port of Unity's
 * `AIConversationPresentationModel.cs`.
 *
 * Presentation-only queue for tool cards, `present_options` rounds, guardrail
 * notices, and document CITATIONS. It is NOT the chat transcript — the panel
 * owns its history entries (mirroring Unity's
 * `AIPanel.ConversationHistoryEntry`).
 */

import { Emitter } from "@core/events/emitter";
import type { AIFileCitation } from "@ai/context/AIContextEngineModel.KnowledgeTransport";

export interface AIToolExecutionPresentation {
  readonly entryId: number;
  readonly toolName: string;
  readonly source: string;
  readonly result: string | null;
}

export interface AIPresentOptionsRound {
  readonly question: string;
  readonly options: ReadonlyArray<string>;
}

export interface AIPresentOptionsPresentation {
  readonly entryId: number;
  readonly rounds: ReadonlyArray<AIPresentOptionsRound>;
  readonly moreInfoRequired: boolean;
}

export interface AIGuardrailNoticePresentation {
  readonly entryId: number;
  readonly message: string;
  readonly severity: "info" | "warning" | "error";
}

/**
 * One document source surfaced with an answer — the web equivalent of Unity's
 * `PublishCitations(IReadOnlyList<AIFileCitation>)`.
 */
export interface AICitationPresentation {
  readonly entryId: number;
  readonly fileId: string;
  readonly fileName: string | null;
  /** `docs/{fileName}` — what `IProjectDocsModel.openAsync` takes. */
  readonly storagePath: string | null;
  /**
   * The verified quotes that earned this document its place under the answer,
   * each with the statement of the answer it supports. Set only on a reference
   * the attribution check kept (`AIAnswerAttribution.ts`). Web-only.
   *
   * Carried, not shown: the chip still renders the document alone. Showing the
   * quote is Phase 2 of the Spark Answer References design and needs no further
   * model work, because the check already returns and verifies these.
   */
  readonly supports?: ReadonlyArray<AICitationSupport>;
}

/** One statement of an answer and the passage words that state it. Web-only. */
export interface AICitationSupport {
  readonly statement: string;
  /** Exact words from the passage, verified present in it. */
  readonly quote: string;
}

/**
 * One passage a document search returned, held as a CANDIDATE reference until
 * the answer it fed is known. Web-only.
 *
 * Retrieved and cited are separate lists: the search decides what Spark READS,
 * and only the attribution check decides what the user SEES. So a search no
 * longer publishes chips; it publishes these, and `useSpark` hands them to the
 * check once the answer settles.
 */
export interface AICitationCandidate {
  readonly fileId: string;
  /** The uploaded file's name: identity, never a label. */
  readonly fileName: string | null;
  /** The Define-app title, when the library knows one. */
  readonly displayName: string | null;
  readonly storagePath: string | null;
  /** The ranker's score, 0-1, or null when the API reported none. */
  readonly score: number | null;
  /** The passage text the check quotes from. */
  readonly text: string;
}

/** The passages one search returned, and the query it searched with. */
export interface AICitationCandidateBatch {
  readonly entryId: number;
  readonly query: string;
  readonly passages: ReadonlyArray<AICitationCandidate>;
}

/**
 * A source line under an answer — where a figure came from. The counterpart of
 * the citation chips, for facts that were MEASURED rather than looked up in a
 * document.
 *
 * It exists because the realtime session runs `output_modalities: ["audio"]`,
 * so everything the model writes is spoken: the Answer skill's
 * `Measured in the scene` attribution has to be published by the tool that
 * produced the figure, or Spark reads "Source: Measured in the scene" aloud
 * after every measurement.
 */
export interface AIAttributionPresentation {
  readonly entryId: number;
  readonly text: string;
}

/** What a knowledge tool actually searched, for the debug tool card. */
export interface AIToolUsageAnnotation {
  readonly prompt?: string;
  /** `"string search"` | `"semantic search"` | `"llm"` (Unity's literals). */
  readonly source?: string;
}

export class AIConversationPresentationModel {
  readonly onToolExecutionsChanged = new Emitter<void>();
  readonly onPresentOptionsChanged = new Emitter<void>();
  readonly onGuardrailNoticesChanged = new Emitter<void>();
  readonly onCitationsChanged = new Emitter<void>();
  readonly onCitationCandidatesChanged = new Emitter<void>();
  readonly onAttributionsChanged = new Emitter<void>();
  /**
   * A tool asked the UI to OPEN a document (Unity
   * `RequestCitationOpen` — the `Find_and_Show_PDF` path, which is the same
   * action as the user clicking a citation chip).
   */
  readonly onCitationOpenRequested = new Emitter<AICitationPresentation>();

  private m_nextEntryId = 1;
  private m_pendingToolExecutions: AIToolExecutionPresentation[] = [];
  private m_pendingOptions: AIPresentOptionsPresentation[] = [];
  private m_pendingNotices: AIGuardrailNoticePresentation[] = [];
  private m_pendingCitations: AICitationPresentation[] = [];
  private m_pendingCandidates: AICitationCandidateBatch[] = [];
  private m_pendingAttributions: AIAttributionPresentation[] = [];
  /** toolName → what it searched; drained by the tool card. */
  private readonly m_toolUsage = new Map<string, AIToolUsageAnnotation>();

  publishToolExecution(toolName: string, source: string, result: string | null): void {
    this.m_pendingToolExecutions.push({
      entryId: this.m_nextEntryId++,
      toolName,
      source,
      result,
    });
    this.onToolExecutionsChanged.emit();
  }

  publishPresentOptions(
    rounds: ReadonlyArray<AIPresentOptionsRound>,
    moreInfoRequired: boolean,
  ): void {
    this.m_pendingOptions.push({
      entryId: this.m_nextEntryId++,
      rounds,
      moreInfoRequired,
    });
    this.onPresentOptionsChanged.emit();
  }

  publishGuardrailNotice(message: string, severity: "info" | "warning" | "error"): void {
    this.m_pendingNotices.push({ entryId: this.m_nextEntryId++, message, severity });
    this.onGuardrailNoticesChanged.emit();
  }

  /** Drain queues — consumers pull pending entries exactly once. */
  consumePendingToolExecutions(): ReadonlyArray<AIToolExecutionPresentation> {
    const pending = this.m_pendingToolExecutions;
    this.m_pendingToolExecutions = [];
    return pending;
  }

  consumePendingPresentOptions(): ReadonlyArray<AIPresentOptionsPresentation> {
    const pending = this.m_pendingOptions;
    this.m_pendingOptions = [];
    return pending;
  }

  consumePendingGuardrailNotices(): ReadonlyArray<AIGuardrailNoticePresentation> {
    const pending = this.m_pendingNotices;
    this.m_pendingNotices = [];
    return pending;
  }

  // ---- Citations (documents) ----------------------------------------

  /** Unity `PublishCitations` — de-duplicated by storage path / file id. */
  publishCitations(citations: ReadonlyArray<AIFileCitation>): void {
    const seen = new Set(
      this.m_pendingCitations.map((c) => (c.storagePath ?? c.fileId).toLowerCase()),
    );
    let added = false;
    for (const citation of citations) {
      const key = (citation.storagePath ?? citation.fileId).toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      this.m_pendingCitations.push({
        entryId: this.m_nextEntryId++,
        fileId: citation.fileId,
        fileName: citation.fileName,
        storagePath: citation.storagePath,
      });
      added = true;
    }
    if (added) this.onCitationsChanged.emit();
  }

  consumePendingCitations(): ReadonlyArray<AICitationPresentation> {
    const pending = this.m_pendingCitations;
    this.m_pendingCitations = [];
    return pending;
  }

  /**
   * The passages a document search returned, as candidate references for the
   * answer being composed. Nothing is shown from this; `useSpark` passes the
   * batch to the attribution check when the answer settles.
   */
  publishCitationCandidates(query: string, passages: ReadonlyArray<AICitationCandidate>): void {
    if (passages.length === 0) return;
    this.m_pendingCandidates.push({ entryId: this.m_nextEntryId++, query, passages: [...passages] });
    this.onCitationCandidatesChanged.emit();
  }

  consumePendingCitationCandidates(): ReadonlyArray<AICitationCandidateBatch> {
    const pending = this.m_pendingCandidates;
    this.m_pendingCandidates = [];
    return pending;
  }

  // ---- Attribution (measured facts) ---------------------------------

  /**
   * Publish the source line for the answer being composed. De-duplicated by
   * text, because one turn can measure several parts and repeating the same
   * line per part says nothing extra.
   */
  publishAttribution(text: string): void {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (this.m_pendingAttributions.some((a) => a.text === trimmed)) return;
    this.m_pendingAttributions.push({ entryId: this.m_nextEntryId++, text: trimmed });
    this.onAttributionsChanged.emit();
  }

  consumePendingAttributions(): ReadonlyArray<AIAttributionPresentation> {
    const pending = this.m_pendingAttributions;
    this.m_pendingAttributions = [];
    return pending;
  }

  // ---- Citations (documents), continued ------------------------------

  /** Unity `RequestCitationOpen` — `Find_and_Show_PDF` opens without a click. */
  requestCitationOpen(citation: AIFileCitation): void {
    this.onCitationOpenRequested.emit({
      entryId: this.m_nextEntryId++,
      fileId: citation.fileId,
      fileName: citation.fileName,
      storagePath: citation.storagePath,
    });
  }

  // ---- Tool usage annotations ---------------------------------------

  /** Unity `PublishToolUsagePrompt` — the composed prompt a tool searched with. */
  publishToolUsagePrompt(toolName: string, prompt: string): void {
    this.m_toolUsage.set(toolName, { ...this.m_toolUsage.get(toolName), prompt });
  }

  /** Unity `PublishToolUsageSource` — `"string search"` / `"semantic search"` / `"llm"`. */
  publishToolUsageSource(toolName: string, source: string): void {
    this.m_toolUsage.set(toolName, { ...this.m_toolUsage.get(toolName), source });
  }

  /** Read-and-clear, so a later run of the same tool can't show a stale note. */
  takeToolUsage(toolName: string): AIToolUsageAnnotation | null {
    const usage = this.m_toolUsage.get(toolName) ?? null;
    this.m_toolUsage.delete(toolName);
    return usage;
  }
}
